import { Button, CardBackground, Typography } from "@hellocarbo/lasagne";
import { SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionFunctionArgs,
  Form,
  Link,
  LoaderFunctionArgs,
  redirectDocument,
  useLoaderData,
  useSubmit,
} from "react-router";
import invariant from "tiny-invariant";

import { SimulationCategory } from "@/components/Simulation";
import PageWrapper from "@/components/ui/PageWrapper";
import { TCategoryName, TSubCategoryStatus } from "@/types";

import { useSimulationContext } from "./Simulation";

export async function loader({ params }: LoaderFunctionArgs) {
  const { simulationId } = params;
  invariant(simulationId, "No simulation id found.");

  const response = await fetch(
    `/api/client/simulations/${simulationId}/categories`
  );

  if (response.status !== 200) {
    return { ok: false };
  }

  const { data: categories } = (await response.json()) as {
    data: Record<TCategoryName, TSubCategoryStatus>;
  };

  return { ok: true, categories };
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { simulationId } = params;
  invariant(simulationId, "No simulation id found.");

  const formData = await request.formData();
  const details_url = formData.get("details_url") as string;
  const checkedCategories = formData.getAll("categories");
  const allCategories = formData.getAll("allCategories");
  const uncheckedCategories = allCategories.filter(
    (category) => !checkedCategories.includes(category)
  );

  const response = await fetch(
    `/api/client/simulations/${simulationId}/categories`,
    {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        enabled: checkedCategories,
        disabled: uncheckedCategories,
      }),
      method: "PATCH",
    }
  );

  if (response.status !== 200) {
    return { ok: false };
  }

  return redirectDocument(details_url);
}

function SimulationCategories() {
  const { categories: defaultCategories } = useLoaderData<typeof loader>();
  const { simulation } = useSimulationContext();
  const { t } = useTranslation(["common", "simulation"]);
  const submit = useSubmit();
  const [categories, setCategories] = useState(defaultCategories);

  invariant(simulation, "No simulation id found!");
  invariant(categories, "No categories found!");

  const handleSubmit = (
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>
  ) => {
    event.preventDefault();

    const form = event.currentTarget;

    const formData = new FormData(form);

    // We need to pass the redirection url to the action
    formData.set("details_url", simulation.details_url);

    // We need to pass all the sub-categories to the action
    Object.values(categories).forEach((subCategories) =>
      Object.keys(subCategories).forEach((subCategoryName) =>
        formData.append("allCategories", subCategoryName)
      )
    );
    submit(formData, { method: "PATCH" });
  };

  const updateAllStates = (newState: boolean) => {
    const res = {} as Record<TCategoryName, TSubCategoryStatus>;
    (Object.keys(categories) as Array<TCategoryName>).forEach((categoryName) =>
      Object.keys(categories[categoryName]).forEach((subCategoryName) => {
        if (!res[categoryName]) {
          res[categoryName] = {};
        }
        res[categoryName][subCategoryName] = newState;
      })
    );
    setCategories(res);
  };

  const checkAll = () => updateAllStates(true);
  const uncheckAll = () => updateAllStates(false);

  return (
    <PageWrapper>
      <div className="mx-xl my-lg">
        <Typography step="3" as="h1" weight="bold" className="mb-md">
          {t("simulation:simulationParameters")}
        </Typography>
        <CardBackground>
          <Typography as="h2" step="2" weight="bold" className="mb-xs">
            {t("simulation:modifyCategories.title")}
          </Typography>
          <div className="mb-md flex items-center justify-between">
            <Typography>
              {t("simulation:modifyCategories.description")}
            </Typography>
            <div className="gap-x-xs flex items-center">
              <Button shape="link" intent="secondary" onClick={checkAll}>
                {t("simulation:selectAll")}
              </Button>
              <Button shape="link" intent="negative" onClick={uncheckAll}>
                {t("simulation:deselectAll")}
              </Button>
            </div>
          </div>
          <Form method="PATCH" onSubmit={handleSubmit}>
            <div className="gap-md grid grid-cols-3">
              {Object.entries(categories).map(
                ([categoryName, subCategories]) => (
                  <SimulationCategory
                    category={categoryName as TCategoryName}
                    subCategories={subCategories}
                    inputName="categories"
                    key={categoryName}
                    onChange={(newSubCategory) =>
                      setCategories({
                        ...categories,
                        [categoryName]: {
                          ...categories[categoryName as TCategoryName],
                          ...newSubCategory,
                        },
                      })
                    }
                  />
                )
              )}
            </div>
            <div className="mt-lg gap-x-md flex items-center justify-center">
              <Button
                shape="link"
                as={Link}
                to={`/simulations/${simulation.id}/parameters`}
              >
                {t("common:back")}
              </Button>
              <Button type="submit">{t("common:next")}</Button>
            </div>
          </Form>
        </CardBackground>
      </div>
    </PageWrapper>
  );
}

export default SimulationCategories;
